import { faComment, faCommentSlash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import i18n, { t } from 'i18next'
import { MessageSeverity } from 'primereact/api'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Fieldset } from 'primereact/fieldset'
import {
  FileUpload,
  FileUploadHandlerParam,
  FileUploadHeaderTemplateOptions,
} from 'primereact/fileupload'
import { InputTextarea } from 'primereact/inputtextarea'
import { Message, MessageSeverityType } from 'primereact/message'
import { SelectButton, SelectButtonChangeParams } from 'primereact/selectbutton'
import { Toast } from 'primereact/toast'
import { Tooltip } from 'primereact/tooltip'
import React, { useEffect, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  API_TRIAL_PERIOD_REPORT,
  API_TRIAL_PERIOD_REPORT_DOWNLOAD,
  API_TRIAL_PERIOD_REPORT_FILE,
  API_TRIAL_PERIOD_REPORT_GENERATE,
  API_TRIAL_PERIOD_REPORT_UPLOAD,
  API_TRIAL_PERIOD_REPORTS,
  MAX_FILE_SIZE,
} from '../../../../../constants'
import {
  RecommendationCheckbox,
  TrialPeriodRating,
} from '../../../../../model/assessment.model'
import {
  RequestMethod,
  Status,
  TrialReportRating,
} from '../../../../../model/enums/enums'
import { SignedUrlResponse } from '../../../../../model/signedUrlResponse.model'
import { TrialPeriodReport } from '../../../../../model/trialPeriodReport.model'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import {
  addTrialPeriodReport,
  updateTrialPeriodReport,
} from '../../../../../redux/official-document/trial-period-report/trialPeriodReportActions'
import { AuthRoutes } from '../../../../../router/routes'
import { handleRequest } from '../../../../../services/api/base/handleRequest'
import { handleFileUpload } from '../../../../../services/api/handleFileUpload'
import { getFormattedDateForMySQL, isSet } from '../../../../../util/formatting'
import { trimSpaces } from '../../../../../util/trimSpaces'
import { BackButton } from '../../../../util/button/BackButton'
import { AllowPopupDialog } from '../../../../util/dialog/AllowPopupDialog'
import { DeleteConfirmationDialog } from '../../../../util/dialog/DeleteConfirmationDialog'
import { ApprenticeDropdown } from '../../../../util/form/dropdown/person-dropdown/ApprenticeDropdown'
import { EmployeeDropdown } from '../../../../util/form/dropdown/person-dropdown/EmployeeDropdown'
import {
  cancelOptions,
  chooseOptions,
  uploadOptions,
} from '../../../../util/form/MokaiUpload'
import { uploadEmptyTemplate } from '../../../../util/form/UploadEmptyFileTemplate'
import { uploadFileTemplate } from '../../../../util/form/UploadFileTemplate'
import { headerTemplate } from '../../../../util/form/UploadHeader'

const defaultReport: TrialPeriodReport = {
  trial_period_report_id: undefined,
  id_apprentice: undefined,
  id_responsible: undefined,
  is_discussed: 1, // 0 = true, 1 = false/default is false
  signed_file_reference: '',
  overall_impression: '',
  professional_competence_rating: undefined,
  professional_competence_text: '',
  methodological_competence_rating: undefined,
  methodological_competence_text: '',
  social_competence_rating: undefined,
  social_competence_text: '',
  self_competence_rating: undefined,
  self_competence_text: '',
  passed_status: undefined,
  additional_comment: '',
  discussion_date: undefined,
  file_name: '',
  created_on: undefined,
  last_change: undefined,
  created_by: undefined,
}

/** This component lets you create and edit a trial period report
 * It allows you to create, upload and download the corresponding pdfs
 */
export const TrialPeriodReportEdit = (): JSX.Element => {
  const [showPopupBlocked, setShowPopupBlocked] = useState<boolean>(false)
  const [blockedLink, setBlockedLink] = useState<string | undefined>(undefined)

  // we add the default report so that we are instantly ready for db
  // this limitation comes from the usage of own route which does not support props
  const [tpr, setTpr] = useState<TrialPeriodReport>(defaultReport)
  const [recommendedHelperText, setRecommendedHelperText] = useState<string>('')
  const [isGeneratingPdf, setIsGeneratingPDF] = useState<boolean>(false)
  const [uploadDialogVisible, setUploadDialogVisible] = useState<boolean>(false)
  const [validDropArea, setValidDropArea] = useState<boolean>(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
  const infoToast = useRef<Toast>(null)
  const fileUploadRef = useRef<FileUpload>(null)

  const [openDeleteDialogue, setOpenDeleteDialogue] = useState<boolean>(false)

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    defaultValues: defaultReport,
  })

  const reports = useAppSelector((state) => state.trialPeriodReports)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatcher = useAppDispatch()

  const fetchFromDb = async (id: number) => {
    const res = await handleRequest<TrialPeriodReport>(
      API_TRIAL_PERIOD_REPORT(id),
      RequestMethod.GET
    ).catch((err) => {
      console.log(`TPR could not be loaded with error: ${err}`)
      navigate(AuthRoutes.trialPeriodReport)
    })

    if (res?.error) {
      console.log(`TPR could not be loaded with error: ${res.error.message}`)
      navigate(AuthRoutes.trialPeriodReport)
      return
    }

    if (res && res.data) {
      setTpr(res.data)
      methods.reset(res.data)
      setRecommendationText(res.data)
    }
  }

  // get the current tpr from the db (direct link)
  // or from the redux store (coming from the overview)
  const fetchTpr = async (id: number) => {
    if (reports.length === 0) {
      await fetchFromDb(id)
    } else {
      const res = reports.find((e) => e.trial_period_report_id === id)
      // we expect this to happen very rarely (in case the redux is not ready yet)
      if (!res) {
        console.warn(
          '[Trial Period Report Edit] ID was not found in redux store, trying db'
        )
        await fetchFromDb(id)
      } else {
        setTpr(res)
        methods.reset(res)
        setRecommendationText(res)
      }
    }

    // in case we came here by direct link we need to make sure that all the reports are loaded
    // to prevent the overview from only showing the editet one when going back
    await checkAllTprs()
  }

  // loads all tprs in case they were not already loaded
  const checkAllTprs = async () => {
    if (reports.length === 0) {
      const allTprs = await handleRequest<TrialPeriodReport[]>(
        API_TRIAL_PERIOD_REPORTS(),
        RequestMethod.GET
      )
      if (allTprs.data) {
        // double loading in strict mode
        allTprs.data.forEach((report) => {
          dispatcher(addTrialPeriodReport(report))
        })
      }
    }
  }

  const getStatusByRating = (rating: number): string => {
    if (rating >= minPointsPassed) {
      return `"${t<string>('core.document.status.passed')}"`
    }
    if (rating >= minPointsExtended && rating < minPointsPassed) {
      return `"${t<string>('core.document.status.extended')}"`
    }
    //if(cumulatedRating < minPointsExtended)
    return `"${t<string>('core.document.status.failed')}"`
  }

  const setRecommendationText = (vals: TrialPeriodReport) => {
    // this is not very nice but since default is set as undefined
    // and active deselection is null we need to check for both
    if (
      vals.methodological_competence_rating !== undefined &&
      vals.methodological_competence_rating !== null &&
      vals.professional_competence_rating !== undefined &&
      vals.professional_competence_rating !== null &&
      vals.social_competence_rating !== undefined &&
      vals.social_competence_rating !== null &&
      vals.self_competence_rating !== undefined &&
      vals.self_competence_rating !== null
    ) {
      const recommendedRating =
        vals.methodological_competence_rating +
        vals.professional_competence_rating +
        vals.social_competence_rating +
        vals.self_competence_rating
      const recommendedStatus = getStatusByRating(recommendedRating)
      const text = t<string>('trial_period_reports.conclusion_recommendation', {
        status: recommendedStatus,
      })
      setRecommendedHelperText(text)
    } else {
      setRecommendedHelperText('')
    }
  }

  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      if (type === 'change') {
        setHasUnsavedChanges(true)

        if (name) {
          setTpr({
            ...tpr,
            ...methods.getValues(),
          })
        }

        const vals = methods.getValues()

        setRecommendationText(vals)
      }
    })
    return () => subscription.unsubscribe()
  }, [methods.watch])

  useEffect(() => {
    const substrings = location.pathname.split('/')
    const tprId = substrings[substrings.length - 1]
    // in case we access directly by link,
    // we need to load all tprs
    // otherwise on save, one tpr will be present in the redux store
    // and only this one will be shown in the overview after
    if (tprId === '+') {
      checkAllTprs()
      return
    }
    if (!parseInt(tprId)) {
      console.info('TPR Entry: Not a valid ID')
      navigate(AuthRoutes.trialPeriodReport)
      return
    }

    fetchTpr(parseInt(tprId))
  }, [location])

  const recommendationOptions: RecommendationCheckbox[] = [
    { label: t<string>('core.document.status.passed'), value: Status.passed },
    {
      label: t<string>('core.document.status.extended'),
      value: Status.extended,
    },
    { label: t<string>('core.document.status.failed'), value: Status.failed },
  ]

  // Points per Rating
  // insufficient = 0, sufficient = 1, good = 2, very-good = 3
  const ratings: TrialPeriodRating[] = [
    {
      value: 3,
      rating: TrialReportRating.very_good,
      label: t<string>('core.rating.very_good'),
    },
    {
      value: 2,
      rating: TrialReportRating.good,
      label: t<string>('core.rating.good'),
    },
    {
      value: 1,
      rating: TrialReportRating.sufficient,
      label: t<string>('core.rating.sufficient'),
    },
    {
      value: 0,
      rating: TrialReportRating.insufficient,
      label: t<string>('core.rating.insufficient'),
    },
  ]

  // The number of points that need to be reached to extend the trial period
  const minPointsExtended = 3

  // The number of points that need to be reached to pass the trial period
  const minPointsPassed = 5

  /**
   * Set a label depending if the trial period report has been discussed yet or not
   * @returns {JSX.Element}
   */
  const getDiscussedLabel = () => {
    return tpr.is_discussed === 0 ? (
      <span>
        {' '}
        {t<string>('core.held')}
        <FontAwesomeIcon
          className={'icon-green ml-2 fa-comment'}
          icon={faComment}
        />
      </span>
    ) : (
      <span>
        {' '}
        {t<string>('core.not_yet_taken_place')}
        <FontAwesomeIcon className={'icon-orange ml-2'} icon={faCommentSlash} />
      </span>
    )
  }

  const getInformationLink = () => {
    if (i18n.language !== 'de' && i18n.language !== 'fr') {
      return (
        'https://www.berufsbildung.ch/dyn/11014.aspx?lang=' +
        i18n.language.toUpperCase() +
        '&action=detail&value=676&lex=0'
      )
    }
    return 'https://www.berufsbildung.ch/dyn/11014.aspx?lang=DE&action=detail&value=676&lex=0'
  }

  /** Returns a warning if a passed_status is set
   * and it is not passed
   */
  const getConclusionComment = () => {
    let infoText: JSX.Element
    let severity: MessageSeverityType
    const passedStatus = methods.getValues('passed_status')
    if (!passedStatus) {
      // display no message if status is undefined or passed
      return <div className={'mr-4 mb-1 pb-1 p-2'} />
    }
    switch (passedStatus) {
      case Status.extended:
        severity = MessageSeverity.WARN
        infoText = (
          <>
            {`${t<string>(
              'trial_period_reports.contact_authority_period_extended'
            ).slice(0, -1)} (`}
            <a target={'_blank'} href={getInformationLink()} rel="noreferrer">
              {t<string>('core.more_information')}
            </a>
            {').'}
          </>
        )
        break
      case Status.failed:
        infoText = (
          <>
            {`${t<string>(
              'trial_period_reports.contact_authority_period_failed'
            ).slice(0, -1)} (`}
            <a target={'_blank'} href={getInformationLink()} rel="noreferrer">
              {t<string>('core.more_information')}
            </a>
            {/** todo Not nice, maybe we want to rethink the design of the entry in the translation db*/}
            {').'}
          </>
        )
        severity = MessageSeverity.ERROR
        break
      case Status.passed:
      default:
        // display no message if status is undefined or passed
        return <div className={'mb-1 pb-1 p-2'} />
    }
    return (
      <Message
        severity={severity}
        text={infoText}
        className={'fas fa question-circle mr-4 w-full'}
      />
    )
  }

  /** Callback when hitting save and generate
   * Submits the form to the backend (save)
   * And generates/downloads a pdf if submission was successful
   */
  const saveAndGeneratePdf = async () => {
    if (
      !methods.getValues('id_responsible') ||
      !methods.getValues('id_apprentice')
    ) {
      methods.trigger()
      return
    }
    setIsGeneratingPDF(true)
    const res = await onSubmit()

    // should not happen in normal circumstances
    // only on backend errors
    if (res <= 0) {
      setIsGeneratingPDF(false)
      infoToast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.error.save_and_generate.title'),
        detail: t<string>('core.error.save_and_generate.message'),
        life: 12000,
      })
      return
    }

    setIsGeneratingPDF(true)
    const pdfGenResult = await handleRequest<any>(
      API_TRIAL_PERIOD_REPORT_GENERATE(res),
      RequestMethod.GET
    )
    setIsGeneratingPDF(false)
    const url = pdfGenResult.data?.url
    if (!url || pdfGenResult.error) {
      console.warn(
        '[Trial Period Report] Error occurred generating the report.'
      )
      infoToast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.error.save_and_generate.title'),
        detail: t<string>('core.error.save_and_generate.message'),
        life: 12000,
      })
      return
    }

    const newWindow: Window | null = window.open(url, '_blank')
    if (newWindow) {
      newWindow.location = url
      newWindow.opener = null
      return
    } else {
      console.warn('Popup blocked!')
      setBlockedLink(url)
      setShowPopupBlocked(true)
    }
  }

  const deleteSignedFile = async () => {
    const reportId = methods.getValues('trial_period_report_id')
    console.log(reportId)
    // should not happen
    if (!reportId) {
      return
    }
    // add right api call here
    const res = await handleRequest(
      API_TRIAL_PERIOD_REPORT_FILE(reportId),
      RequestMethod.DELETE
    )
    if (res.httpStatus === 200) {
      const curTpr = { ...tpr }
      curTpr.signed_file_reference = undefined
      methods.setValue('signed_file_reference', undefined)
      setTpr(curTpr)
      dispatcher(updateTrialPeriodReport(curTpr))
    }
  }

  /** For file removal */
  const onTemplateRemove = (file: any, callback: any) => {
    callback()
  }

  /** If upload is successful, set a dummy value to signed file reference
   * So it can be seen by the application
   */
  const handleUpload = (id: number | undefined): void => {
    if (!id) return
    const curTpr = { ...tpr }
    // dummy value
    curTpr.signed_file_reference = 'created'
    dispatcher(updateTrialPeriodReport(curTpr))
    setTpr(curTpr)
    setUploadDialogVisible(false)

    infoToast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.save.success.title'),
      detail: t<string>('core.action.save.success.message'),
      life: 4000,
    })
  }

  const getUploadUrl = () => {
    if (!tpr?.trial_period_report_id) {
      return undefined
    }
    return API_TRIAL_PERIOD_REPORT_UPLOAD(tpr.trial_period_report_id)
  }

  const fileUploadProps = {
    handleUploadResult: handleUpload,
    uploadUrl: getUploadUrl(),
    documentId: tpr?.trial_period_report_id,
  }

  const itemTemplate = uploadFileTemplate(onTemplateRemove)

  const downloadSignedReport = async () => {
    const id = methods.getValues('trial_period_report_id')
    if (!id) {
      infoToast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.error.download.title'),
        detail: t<string>('core.error.download.message'),
        life: 12000,
      })
      return
    }
    const downloadLinkResult = await handleRequest<SignedUrlResponse>(
      API_TRIAL_PERIOD_REPORT_DOWNLOAD(id),
      RequestMethod.GET
    )

    const signedUrl = downloadLinkResult?.data?.url
    if (signedUrl) {
      window.open(signedUrl, '_blank')
      return
    }
    infoToast.current?.show({
      closable: true,
      severity: 'error',
      summary: t<string>('core.error.download.title'),
      detail: t<string>('core.error.download.message'),
      life: 12000,
    })
  }

  const uploadButtonDisabled = () => {
    return (
      !methods.getValues('trial_period_report_id') ||
      tpr.passed_status === null ||
      tpr.passed_status === Status.open ||
      hasUnsavedChanges
    )
  }

  const callFileUpload = async (e: FileUploadHandlerParam) => {
    const res = await handleFileUpload(fileUploadProps)(e)
    // -1 means not a pdf
    if (res === -1) {
      infoToast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.error.upload.file_type.pdf.title'),
        detail: t<string>('core.error.upload.file_type.pdf.message'),
        life: 12000,
      })
    }
    // should not happen
    else if (!res) {
      infoToast.current?.show({
        closable: true,
        severity: 'error',
        summary: t<string>('core.error.add.failed.title'),
        detail: t<string>('core.error.add.failed.message'),
        life: 12000,
      })
    }
  }
  /** Submits the current form
   * Returns -1 iff the submission fails
   * the number of the submitted report otherwise
   */
  const onSubmit = async (): Promise<number> => {
    let formValues = methods.getValues()
    // for db format
    formValues.created_on = undefined
    formValues.last_change = undefined

    // we need to fix these fields since null (maybe wrongly) is not allowed in the backend
    if (formValues.passed_status === null) {
      formValues.passed_status = Status.open
    }

    for (let field of Object.keys(formValues)) {
      // @ts-ignore
      formValues[field] = trimSpaces(formValues[field])
    }

    // format date for mysql date type
    if (isSet(formValues.discussion_date)) {
      formValues.discussion_date = getFormattedDateForMySQL(
        formValues.discussion_date
      )
    }

    let newTpr: TrialPeriodReport

    // if no db entry exists
    if (!formValues.trial_period_report_id) {
      const res = await handleRequest<TrialPeriodReport>(
        API_TRIAL_PERIOD_REPORTS(),
        RequestMethod.POST,
        formValues
      )

      if (!res.data || res.error) {
        console.warn(
          '[Trial Period Report]: An error occurred when adding the report to the DB.'
        )
        return -1
      }
      newTpr = res.data
      // display the correct link without side effects
      window.history.pushState(
        null,
        '',
        `${AuthRoutes.trialPeriodReport}/${res.data.trial_period_report_id}`
      )

      dispatcher(addTrialPeriodReport(newTpr))

      // entry already exists
    } else {
      const res = await handleRequest<TrialPeriodReport>(
        API_TRIAL_PERIOD_REPORT(formValues.trial_period_report_id),
        RequestMethod.PUT,
        formValues
      )
      if (!res.data || res.error) {
        console.log(
          '[Trial Period]: An error occurred when updating the report in the DB'
        )
        return -1
      }
      newTpr = res.data
      dispatcher(updateTrialPeriodReport(newTpr))
    }

    infoToast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.save.success.title'),
      detail: t<string>('core.action.save.success.message'),
      life: 4000,
    })
    setTpr(newTpr)
    methods.reset(newTpr)
    if (newTpr.trial_period_report_id) {
      setHasUnsavedChanges(false)
    }
    // we need to return the tpr here
    return newTpr.trial_period_report_id || -1
  }

  return (
    <>
      <div className="card pl-2 pr-2 pb-4">
        <BackButton
          translationKey={'navigation.go_back_overview'}
          route={AuthRoutes.trialPeriodReport}
        />
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className={'formgrid grid'}
          >
            <div
              className={
                'field col-12 md:col-6 w-full inline-flex flex-wrap pr-0'
              }
            >
              <div className={'md:col-6 col-12'}>
                <ApprenticeDropdown isDisabled={!!tpr.signed_file_reference} />
              </div>
              <div className={'md:col-6 col-12'}>
                <EmployeeDropdown
                  fieldName={'id_responsible'}
                  isDisabled={!!tpr.signed_file_reference}
                  isCompulsory={true}
                />
              </div>
            </div>
            <div className="field col-12 md:col-12">
              <label htmlFor={'overall_impression'}>
                {t<string>('trial_period_reports.overall_impression')}
              </label>
              <Controller
                name="overall_impression"
                control={methods.control}
                render={({ field }) => (
                  <InputTextarea
                    id={field.name}
                    {...field}
                    value={field.value || ''}
                    rows={1}
                    className={'col-12 p-1'}
                    autoResize={true}
                    placeholder={t<string>(
                      'trial_period_reports.overall_impression_placeholder'
                    )}
                    disabled={!!tpr.signed_file_reference}
                  />
                )}
              />
            </div>

            <Fieldset
              toggleable
              legend={t<string>('core.assessment.type.professional_competence')}
              className={'mb-2 mr-2 w-full'}
            >
              <div className={'formgrid grid p-fluid gap-2 align-items-center'}>
                <Controller
                  name="professional_competence_rating"
                  control={methods.control}
                  render={({ field }) => (
                    <SelectButton
                      id={field.name}
                      {...field}
                      options={ratings}
                      className={'col-12 md:col-6 pl-0'}
                      onChange={(e: SelectButtonChangeParams) => {
                        field.onChange(e)
                      }}
                      disabled={!!tpr.signed_file_reference}
                    />
                  )}
                />
                <label className={'secondary-text small-text col'}>
                  {t<string>(
                    'core.assessment.description.professional_competence'
                  )}
                </label>
                <Controller
                  name="professional_competence_text"
                  control={methods.control}
                  render={({ field }) => (
                    <InputTextarea
                      id={field.name}
                      placeholder={t<string>('core.reason')}
                      {...field}
                      value={field.value || ''}
                      rows={1}
                      className={'col-12 p-1'}
                      autoResize={true}
                      disabled={!!tpr.signed_file_reference}
                    />
                  )}
                />
              </div>
            </Fieldset>
            <Fieldset
              toggleable
              className={'mb-2 mr-2 w-full'}
              legend={t<string>(
                'core.assessment.type.methodological_competence'
              )}
            >
              <div className={'formgrid grid p-fluid gap-2 align-items-center'}>
                <Controller
                  name="methodological_competence_rating"
                  control={methods.control}
                  render={({ field }) => (
                    <SelectButton
                      id={field.name}
                      {...field}
                      options={ratings}
                      onChange={(e: SelectButtonChangeParams) => {
                        field.onChange(e)
                        //setRecommendation()
                      }}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 md:col-6 pl-0'}
                    />
                  )}
                />
                <label className={'secondary-text small-text col'}>
                  {t<string>(
                    'core.assessment.description.methodological_competence'
                  )}
                </label>

                <Controller
                  name="methodological_competence_text"
                  control={methods.control}
                  render={({ field }) => (
                    <InputTextarea
                      id={field.name}
                      {...field}
                      value={field.value || ''}
                      placeholder={t<string>('core.reason')}
                      rows={1}
                      autoResize={true}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 p-1'}
                    />
                  )}
                />
              </div>
            </Fieldset>
            <Fieldset
              toggleable
              className={'mb-2 mr-2 w-full'}
              legend={t<string>('core.assessment.type.social_competence')}
            >
              <div className={'formgrid grid p-fluid gap-2 align-items-center'}>
                <Controller
                  name="social_competence_rating"
                  control={methods.control}
                  render={({ field }) => (
                    <SelectButton
                      id={field.name}
                      {...field}
                      options={ratings}
                      onChange={(e: SelectButtonChangeParams) => {
                        field.onChange(e)
                      }}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 md:col-6 pl-0'}
                    />
                  )}
                />
                <label className={'secondary-text small-text col'}>
                  {t<string>('core.assessment.description.social_competence')}
                </label>

                <Controller
                  name="social_competence_text"
                  control={methods.control}
                  render={({ field }) => (
                    <InputTextarea
                      id={field.name}
                      placeholder={t<string>('core.reason')}
                      {...field}
                      value={field.value || ''}
                      rows={1}
                      autoResize={true}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 p-1'}
                    />
                  )}
                />
              </div>
            </Fieldset>
            <Fieldset
              toggleable
              className={'mb-2 mr-2 w-full'}
              legend={t<string>('core.assessment.type.self_competence')}
            >
              <div className={'formgrid grid p-fluid gap-2 align-items-center'}>
                <Controller
                  name="self_competence_rating"
                  control={methods.control}
                  render={({ field }) => (
                    <SelectButton
                      id={field.name}
                      {...field}
                      options={ratings}
                      onChange={(e: SelectButtonChangeParams) => {
                        field.onChange(e)
                        //setRecommendation()
                      }}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 md:col-6 pl-0'}
                    />
                  )}
                />
                <label className={'secondary-text small-text col'}>
                  {t<string>('core.assessment.description.self_competence')}
                </label>
                <Controller
                  name="self_competence_text"
                  control={methods.control}
                  render={({ field }) => (
                    <InputTextarea
                      id={field.name}
                      placeholder={t<string>('core.reason')}
                      {...field}
                      value={field.value || ''}
                      rows={1}
                      autoResize={true}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 p-1'}
                    />
                  )}
                />
              </div>
            </Fieldset>
            <Fieldset
              toggleable
              className={'mb-2 mr-2 w-full'}
              legend={t<string>('trial_period_reports.conclusion')}
            >
              <div className={'formgrid grid p-fluid gap-1'}>
                {/** We only want to show the recommendation  before a signed file is uploaded */}
                <label className={'ml-1 secondary-text small-text col-12'}>
                  {!tpr.signed_file_reference && <p>{recommendedHelperText}</p>}
                </label>
                <div className="col-12 pr-0">
                  <Controller
                    name="passed_status"
                    control={methods.control}
                    render={({ field }) => (
                      <SelectButton
                        id={field.name}
                        {...field}
                        options={recommendationOptions}
                        onChange={(e: SelectButtonChangeParams) => {
                          field.onChange(e)
                        }}
                        disabled={!!tpr.signed_file_reference}
                        className={'col-12 pr-0'}
                      />
                    )}
                  />
                </div>
                <div className="w-full m-0 p-0 inline-flex">
                  <div className="md:col-4 p-0 m-0" />
                  {/** Trick to move the text around */}
                  {methods.getValues('passed_status') === Status.failed && (
                    <div className="md:col-4" />
                  )}
                  <div className="col-12 md:col-4 mr-0 pr-0">
                    {/** Only show the conclusion comment when no file is uploaded */}
                    {!tpr.signed_file_reference && getConclusionComment()}
                  </div>
                </div>
                <Controller
                  name="additional_comment"
                  control={methods.control}
                  render={({ field }) => (
                    <InputTextarea
                      id={field.name}
                      placeholder={t<string>(
                        'trial_period_reports.additional_comment'
                      )}
                      {...field}
                      value={field.value || ''}
                      rows={1}
                      autoResize={true}
                      disabled={!!tpr.signed_file_reference}
                      className={'col-12 p-1'}
                    />
                  )}
                />
              </div>
            </Fieldset>

            <Fieldset
              toggleable
              className={'mb-2 mr-2 w-full'}
              legend={t<string>('trial_period_reports.discussion')}
            >
              <div className={'formgrid grid p-fluid'}>
                <label className={'mb-1 secondary-text small-text col-12'}>
                  {t<string>('trial_period_reports.discussion_text')}
                </label>
                <div className={'inline-flex w-full'}>
                  <Controller
                    name="discussion_date"
                    control={methods.control}
                    render={({ field }) => (
                      <Calendar
                        appendTo={document.body}
                        id={field.name}
                        {...field}
                        placeholder={t<string>('core.document.discussion_date')}
                        value={field.value ? new Date(field.value) : undefined}
                        dateFormat={'dd.mm.yy'}
                        showIcon
                        className={'col-4 pl-0'}
                        onChange={(e) => field.onChange(e)}
                        showButtonBar={true}
                        disabled={!!tpr.signed_file_reference}
                      />
                    )}
                  />

                  <div className="flex col-6 justify-content-start align-items-center">
                    <Controller
                      name="is_discussed"
                      control={methods.control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          name={field.name}
                          className={'mr-2'}
                          inputId={field.name}
                          checked={tpr.is_discussed === 0}
                          onChange={(e) => {
                            setHasUnsavedChanges(true)
                            // for some reason, we need to do this here
                            // it is not completely clear why we cannot do this with onchange
                            // maybe has to do with mismatch between input switch type (bool)
                            // and tpr type (number)
                            if (tpr.is_discussed !== undefined) {
                              methods.setValue(
                                'is_discussed',
                                1 - tpr.is_discussed
                              )
                              setTpr({
                                ...tpr,
                                is_discussed: 1 - tpr.is_discussed,
                              })
                            }
                          }}
                          disabled={!!tpr.signed_file_reference}
                        />
                      )}
                    />
                    <div className={'mb-2 pt-1'}>
                      <label htmlFor={'is_discussed'} className={'pb-2 pt-2'}>
                        {t<string>('trial_period_reports.discussed')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Fieldset>

            {!tpr.signed_file_reference ? (
              <div className={'w-full grid gap-2 align-items-center pl-2 mt-2'}>
                <Button
                  type={'button'}
                  label={
                    hasUnsavedChanges
                      ? t<string>('core.cancel')
                      : t<string>('core.close')
                  }
                  className="col-12 md:col-3 pt-1 pb-1 p-button-text"
                  onClick={() => navigate(AuthRoutes.trialPeriodReport)}
                />
                <Button
                  type={'button'}
                  label={t<string>('trial_period_reports.save_and_generate')}
                  className="col-12 md:col-3 p-1 pl-2 p-button-warning"
                  loading={isGeneratingPdf}
                  onClick={saveAndGeneratePdf}
                />
                <div className={'col-12 md:col-3 pr-0 disabled-upload-button'}>
                  <Button
                    type="button"
                    disabled={uploadButtonDisabled()}
                    onClick={() => setUploadDialogVisible(true)}
                    label={t<string>(
                      'trial_period_reports.upload_signed_report'
                    )}
                    className={'w-full pt-1 pb-1 p-button-text pr-0 '}
                  />
                </div>
                {uploadButtonDisabled() && (
                  <Tooltip
                    content={t<string>(
                      'trial_period_reports.tooltip_upload_button'
                    )}
                    target={'.disabled-upload-button'}
                    position={'top'}
                    showDelay={500}
                    appendTo={document.body}
                  />
                )}

                <Button
                  type="submit"
                  className={'col-12 md:col pt-1 pb-1'}
                  label={t<string>('core.save_changes')}
                />
              </div>
            ) : (
              <div
                className={
                  'w-full grid gap-2 align-items-center pl-2 mt-2 justify-content-end align-items-center'
                }
              >
                <div className={'col-12 md:col-3 pt-1 pb-1 pr-0'}>
                  <BackButton
                    translationKey={'navigation.go_back_overview'}
                    route={AuthRoutes.trialPeriodReport}
                  />
                </div>
                <Button
                  type={'button'}
                  label={t<string>('core.create_pdf')}
                  className="col-12 md:col-2 pt-1 pb-1 p-button-warning p-button-outlined"
                  loading={isGeneratingPdf}
                  tooltip={t<string>(
                    'trial_period_reports.tooltip_create_again'
                  )}
                  tooltipOptions={{ showDelay: 500, position: 'top' }}
                  onClick={saveAndGeneratePdf}
                />
                <Button
                  type="button"
                  className={'col-12 md:col-3 pt-1 pb-1'}
                  label={`${t<string>('title.trial_period_report')} ${t<string>(
                    'core.overwrite'
                  )}`}
                  tooltip={t<string>('trial_period_reports.tooltip_overwrite')}
                  tooltipOptions={{ showDelay: 500, position: 'top' }}
                  onClick={() => setUploadDialogVisible(true)}
                />
                <Button
                  label={t<string>(
                    'trial_period_reports.download_signed_report'
                  )}
                  type={'button'}
                  icon="fas fa-file-pdf fa-lg"
                  className="col-12 md:col-3 pt-1 pb-1 pl-1 p-button-warning"
                  onClick={() => downloadSignedReport()}
                />

                <Button
                  type={'button'}
                  icon="fa-solid fa-trash"
                  className="col-2 m-0 p-0 p-button-rounded p-button-danger p-button-text"
                  onClick={() => setOpenDeleteDialogue(true)}
                  tooltip={t<string>(
                    'trial_period_reports.delete_file.tooltip'
                  )}
                  tooltipOptions={{
                    appendTo: document.body,
                    position: 'top',
                    showDelay: 500,
                  }}
                />
              </div>
            )}

            <DeleteConfirmationDialog
              onClose={() => setOpenDeleteDialogue(false)}
              onDelete={() => deleteSignedFile()}
              acceptLabelKey={'core.delete'}
              rejectLabelKey={'core.cancel'}
              visible={openDeleteDialogue}
              dataToDelete={undefined}
              title={t<string>('title.trial_period_report')}
              message={t<string>('dialog.delete.question_single_delete')}
            />

            <Dialog
              onHide={() => setUploadDialogVisible(false)}
              id={'upload_dialog'}
              header={`${t<string>('title.trial_period_report')} ${t<string>(
                'core.upload'
              )} `}
              visible={uploadDialogVisible}
            >
              <FileUpload
                ref={fileUploadRef}
                name="educationReport"
                customUpload={true}
                headerTemplate={(options: FileUploadHeaderTemplateOptions) =>
                  headerTemplate(options, true)
                }
                chooseOptions={chooseOptions}
                uploadOptions={uploadOptions}
                cancelOptions={cancelOptions}
                emptyTemplate={uploadEmptyTemplate(
                  setValidDropArea,
                  validDropArea
                )}
                itemTemplate={itemTemplate}
                contentStyle={{ backgroundColor: 'rgb(226, 227,227)' }}
                multiple={false}
                accept={'.pdf'}
                maxFileSize={MAX_FILE_SIZE}
                uploadHandler={(e) => callFileUpload(e)}
              />
            </Dialog>
          </form>
        </FormProvider>
      </div>
      <AllowPopupDialog
        closeDialog={() => {
          setBlockedLink(undefined)
          setShowPopupBlocked(false)
        }}
        showDialog={showPopupBlocked}
        link={blockedLink}
      />
      <Toast ref={infoToast} position={'bottom-right'} />
      <Tooltip position={'top'} target=".disabled-button" />
    </>
  )
}